import { useState } from 'react';
import { regionOptions, typeOptions, updateOptions } from '@/mocks/categories';
import type { FilterState, SortKey } from '@/lib/content';

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
}

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: 'latest', label: '最新', icon: 'ri-time-line' },
  { key: 'hot', label: '最热', icon: 'ri-fire-line' },
  { key: 'recommended', label: '推荐', icon: 'ri-thumb-up-line' },
];

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-foreground-500">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none pl-3 pr-8 py-2 text-sm rounded-md bg-background-50 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 cursor-pointer"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
      </div>
    </label>
  );
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const update = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => update({ sort: opt.key })}
                className={`inline-flex items-center gap-1 px-3 md:px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  filters.sort === opt.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                <i className={opt.icon}></i>
                {opt.label}
              </button>
            ))}
          </div>
          <span className="hidden md:inline text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{resultCount}</strong> 条结果
          </span>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-background-200 text-foreground-700 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选
        </button>
      </div>

      <div className={`${mobileOpen ? 'grid' : 'hidden'} md:grid grid-cols-2 md:grid-cols-3 gap-3 mt-4`}>
        <SelectField label="地区" value={filters.region} options={['全部地区', ...regionOptions]} onChange={(v) => update({ region: v })} />
        <SelectField label="服务类型" value={filters.type} options={['全部类型', ...typeOptions]} onChange={(v) => update({ type: v })} />
        <SelectField label="更新时间" value={filters.updated} options={updateOptions} onChange={(v) => update({ updated: v })} />
      </div>

      <div className={`${mobileOpen ? 'flex' : 'hidden'} md:flex items-center justify-between mt-4 pt-4 border-t border-background-100`}>
        <span className="text-sm text-foreground-500">
          共 <strong className="text-foreground-900">{resultCount}</strong> 条结果
        </span>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}